import React from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, type Document } from '@contentful/rich-text-types';
import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { Button } from '@depop/web-ui-kit/Button';
import { clsx } from 'clsx';

import { ButtonExperiment } from '../ButtonExperiment';
import { ExtendedLink } from '../ExtendedLink';

import styles from './styles.module.css';

import { BannerLocations } from '@/modules/cms/types';
import { TrackingType } from '@/modules/home/types';
import { createCMSTileHref } from '@/modules/home/utils';

type BannerProps = {
  bannerLocation?: BannerLocations;
  title: string;
  body?: Document;
  staticBody?: string;
  cta: {
    copy: string;
    url: string;
    onClick?: () => void;
  };
  desktopBackgroundImage: string;
  mobileBackgroundImage: string;
};

export function Banner({
  bannerLocation = BannerLocations.Home,
  title,
  body,
  staticBody,
  cta: { copy, url, onClick },
  desktopBackgroundImage,
  mobileBackgroundImage,
}: BannerProps) {
  const isSellingHubBanner = bannerLocation === BannerLocations.SellingHub;

  const themeStyle = isSellingHubBanner ? styles.sand : '';

  if (!body && !staticBody) {
    return null;
  }

  function getBody() {
    if (staticBody) {
      return (
        <Caption
          variant="lg-regular"
          className={clsx(styles.bodyParagraph, themeStyle)}
        >
          {staticBody}
        </Caption>
      );
    }

    return documentToReactComponents(body as Document, {
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <Caption
            variant="lg-regular"
            className={clsx(styles.bodyParagraph, themeStyle)}
          >
            {children}
          </Caption>
        ),
      },
    });
  }

  return (
    <div className={styles.container}>
      <div className={clsx(styles.contentWrapper, themeStyle)}>
        {Boolean(title) && (
          <Heading variant="md" className={clsx(styles.heading, themeStyle)}>
            <span>{title}</span>
          </Heading>
        )}
        {getBody()}
        <div className={styles.ctaWrapper}>
          <ExtendedLink
            href={
              isSellingHubBanner
                ? url
                : createCMSTileHref(
                    { link: url, title },
                    TrackingType.skinnyBanner
                  )
            }
          >
            {isSellingHubBanner ? (
              <Button onClick={onClick} className={styles.bannerButton}>
                {copy}
              </Button>
            ) : (
              <ButtonExperiment
                variant="primary"
                inverted
                onClick={onClick}
                legacyOutline
                legacyClassName={styles.bannerButton}
              >
                {copy}
              </ButtonExperiment>
            )}
          </ExtendedLink>
        </div>
      </div>
      <picture>
        <source
          media="(min-width: 768px)"
          srcSet={`${desktopBackgroundImage}?fm=webp&w=1400`}
        />
        <img
          className={styles.bannerImage}
          src={mobileBackgroundImage}
          alt={title}
        />
      </picture>
    </div>
  );
}
